import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Button } from './Button';

export interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  variant?: 'default' | 'danger';
}

interface DropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  align?: 'left' | 'right';
  triggerIcon?: React.ReactNode;
  triggerLabel?: string;
  hideChevron?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  className,
  align = 'right',
  triggerIcon,
  triggerLabel,
  hideChevron = false,
  variant = 'secondary',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={clsx('relative', className)}>
      {/* Trigger button */}
      <Button
        variant={variant}
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center justify-center transition-all whitespace-nowrap flex-shrink-0',
          // 如果只有图标，使其成为正方形并移除内边距
          (!triggerLabel && (triggerIcon || (selectedOption?.icon && !triggerLabel))) ? 'aspect-square !p-0' : 'gap-2',
          className
        )}
      >
        {triggerIcon ? (
          triggerIcon
        ) : selectedOption?.icon && (
          selectedOption.icon
        )}
        {triggerLabel && (
          <span className="hidden sm:inline">{triggerLabel}</span>
        )}
        {!triggerLabel && !triggerIcon && selectedOption?.label && (
          <span>{selectedOption.label}</span>
        )}
        {!hideChevron && (
          <svg
            className={clsx(
              'w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </Button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={clsx(
            'absolute top-full mt-1 z-50',
            'bg-white dark:bg-gray-800',
            'rounded-lg shadow-lg',
            'border border-gray-200 dark:border-gray-700',
            'py-1 min-w-[140px]',
            'animate-fade-in',
            {
              'left-0': align === 'left',
              'right-0': align === 'right',
            }
          )}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              disabled={option.disabled}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-2',
                'text-sm text-left',
                'transition-colors duration-150',
                'focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700',
                {
                  'bg-gray-100 dark:bg-gray-700': option.value === value,
                  'hover:bg-gray-100 dark:hover:bg-gray-700': option.value !== value,
                  'text-gray-900 dark:text-white': !option.disabled && option.variant !== 'danger',
                  'text-red-600 dark:text-red-400': option.variant === 'danger',
                  'text-gray-400 dark:text-gray-500 cursor-not-allowed': option.disabled,
                }
              )}
            >
              {option.icon && (
                <span className={clsx('flex items-center', option.variant === 'danger' && 'text-red-600 dark:text-red-400')}>
                  {option.icon}
                </span>
              )}
              <span>{option.label}</span>
              {option.value === value && (
                <svg
                  className="w-4 h-4 ml-auto text-blue-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
